import { isPlatformBrowser } from '@angular/common';
import {
    Component,
    DestroyRef,
    ElementRef,
    HostListener,
    inject,
    Inject,
    PLATFORM_ID,
    ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

import { Config } from '@src/app/shared/config';
import { WINDOW } from '@src/app/shared/models/tokens';
import { Language } from '@src/app/shared/utils/models';

@Component({
    selector: 'vision2-language-selector',
    templateUrl: './language-selector.component.html',
    styleUrl: './language-selector.component.scss',
    standalone: false,
})
export class LanguageSelectorComponent {
    selectorLanguage = '';
    selectorReady = false;

    language = '';
    languageMenu = false;
    languages: Record<string, Language> = {};
    platformId = inject(PLATFORM_ID);
    private destroyRef = inject(DestroyRef);

    @ViewChild('languageSelect')
    private languageSelectRef?: ElementRef<HTMLSelectElement>;

    constructor(
        @Inject(Config) private config: Config,
        @Inject(WINDOW) private window: Window,
        private router: Router,
    ) {}

    ngOnInit() {
        const languages = {} as Record<string, Language>;
        const availableLanguages: any = this.config.languages;

        for (const index in availableLanguages) {
            if (
                !this.config.languageAliases[index] &&
                !availableLanguages[index].hidden
            ) {
                languages[index] = availableLanguages[index];
            }
        }
        this.languages = languages;

        this.updateSelectorLanguage();

        // route language isn't reflected in the URL until navigation completes, e.g. via back/forward
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => this.updateSelectorLanguage());
    }

    // back/forward restores from the browser's history don't re-run Angular init or change detection,
    // so the native select can be left showing whatever option the user last interacted with
    @HostListener('window:pageshow', ['$event'])
    onPageShow(event: PageTransitionEvent): void {
        if (!event.persisted) {
            return;
        }

        this.updateSelectorLanguage();

        if (this.languageSelectRef) {
            this.languageSelectRef.nativeElement.value = this.selectorLanguage;
        }
    }

    private updateSelectorLanguage(): void {
        const routeLanguage =
            this.router.url.split('/').filter(Boolean)[0] || '';
        const selectedLanguageCandidates = [
            routeLanguage,
            this.config.getCurrentLanguage(),
            this.config.getPreferredLanguage(),
        ].map((language) => this.normalizeLanguageKey(language));

        this.selectorLanguage =
            selectedLanguageCandidates.find(
                (language) => !!this.languages[language],
            ) ||
            Object.keys(this.languages)[0] ||
            '';

        if (isPlatformBrowser(this.platformId)) {
            this.selectorReady =
                !!this.selectorLanguage &&
                Object.keys(this.languages).length > 0;
        }
    }

    normalizeLanguageKey(language: string): string {
        return (language || '').split('-')[0].trim().toLowerCase();
    }

    changeLanguage(event: Event) {
        const lang = (event.target as HTMLSelectElement).value;
        const basePath = this.router.url.split('/').slice(2).join('/');
        const urlReplacementArray = [lang, basePath];
        const newUrl = urlReplacementArray.filter(Boolean).join('/');
        this.window.location.assign(newUrl);
    }
}
